import { memo, useState, useEffect, useRef } from 'react';
import { DateRangePicker as ReactDateRangePicker } from 'react-date-range';
import { LuCalendarDays } from 'react-icons/lu';
import { RiCloseLine } from 'react-icons/ri';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import styles from './styles.module.css';

const DateRangePicker = memo(
  ({
    onDateRangeChange,
    initialStartDate = null,
    initialEndDate = null,
    className,
    businessDay = null,
    ...props
  }) => {
    const [selectionRange, setSelectionRange] = useState({
      startDate: initialStartDate,
      endDate: initialEndDate,
      key: 'selection',
    });
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef(null);

    useEffect(() => {
      setSelectionRange({
        startDate: initialStartDate,
        endDate: initialEndDate,
        key: 'selection',
      });
    }, [initialStartDate, initialEndDate]);

    const handleSelect = (ranges) => {
      setSelectionRange(ranges.selection);
      onDateRangeChange?.(ranges.selection);
    };

    const formatDate = (date) => {
      if (!date) return '';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    };

    const togglePicker = () => {
      setIsOpen(!isOpen);
    };

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          containerRef.current &&
          !containerRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };

      if (isOpen) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [isOpen]);

    const getButtonText = () => {
      if (!selectionRange.startDate || !selectionRange.endDate) {
        return 'Select date range';
      }
      return `${formatDate(selectionRange.startDate)} - ${formatDate(
        selectionRange.endDate
      )}`;
    };

    const handleBackdropClick = () => {
      setIsOpen(false);
    };

    const businessDayPanel =
      businessDay == null ? null : (
        <div className={styles.businessDaySlot}>
          <label className={styles.businessDayField}>
            <span className={styles.businessDayLabel}>Period type</span>
            <select
              className={styles.businessDaySelect}
              value={businessDay.periodType || 'businessDay'}
              onChange={(event) =>
                businessDay.onPeriodTypeChange?.(event.target.value)
              }
            >
              <option value="businessDay">Business Day</option>
              <option value="dateRange">Date range</option>
            </select>
          </label>
          {businessDay.periodType === 'businessDay' && businessDay.empty && (
            <p className={styles.businessDayHelper}>
              No business days in these dates. Widen the dates or switch period
              type.
            </p>
          )}
          {businessDay.periodType === 'businessDay' && !businessDay.empty && (
            <>
              <label className={styles.businessDayField}>
                <span className={styles.businessDayLabel}>Start Business Day</span>
                <select
                  className={styles.businessDaySelect}
                  value={businessDay.startId || ''}
                  onChange={(event) => {
                    if (!event.target.value) return;
                    businessDay.onStartChange?.(event.target.value);
                  }}
                >
                  <option value="">Select</option>
                  {(businessDay.options || []).map((option) => (
                    <option key={`start-${option.id}`} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className={styles.businessDayField}>
                <span className={styles.businessDayLabel}>End Business Day</span>
                <select
                  className={styles.businessDaySelect}
                  value={businessDay.endId || ''}
                  onChange={(event) => {
                    if (!event.target.value) return;
                    businessDay.onEndChange?.(event.target.value);
                  }}
                >
                  <option value="">Select</option>
                  {(businessDay.options || []).map((option) => (
                    <option key={`end-${option.id}`} value={option.id}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
            </>
          )}
        </div>
      );

    return (
      <div
        ref={containerRef}
        className={`${styles.container} ${className || ''}`}
      >
        <button
          className={styles.dateRangeButton}
          onClick={togglePicker}
          type="button"
        >
          <span className={styles.buttonText}>{getButtonText()}</span>
          <LuCalendarDays className={styles.calendarIcon} />
        </button>
        {isOpen && (
          <>
            <div className={styles.pickerWrapper}>
              {businessDayPanel}
              <ReactDateRangePicker
                ranges={[selectionRange]}
                onChange={handleSelect}
                {...props}
              />
            </div>
            <div className={styles.mobileModal}>
              <div className={styles.backdrop} onClick={handleBackdropClick} />
              <div className={styles.perfectCenter}>
                <div className={styles.mobilePickerWrapper}>
                  <div className={styles.mobileHeader}>
                    <button
                      className={styles.closeButton}
                      onClick={handleBackdropClick}
                      type="button"
                      aria-label="Close"
                    >
                      <RiCloseLine className={styles.closeIcon} />
                    </button>
                  </div>
                  {businessDayPanel}
                  <ReactDateRangePicker
                    ranges={[selectionRange]}
                    onChange={handleSelect}
                    {...props}
                  />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  }
);

export default DateRangePicker;
